/**
 * ExecutionProgress - Non-blocking popup showing flow execution progress
 * 
 * Displays in the upper right corner without blocking user interaction.
 * Draggable so the user can move it around.
 */

import { createPortal } from 'react-dom';
import { useExecution } from '../../hooks/useExecution';
import { useState, useRef, useCallback } from 'react';
import './ExecutionProgress.css';

/**
 * ExecutionProgress component - Compact upper-right popup
 */
export function ExecutionProgress() {
  const { 
    state, 
    isExecuting, 
    isPaused, 
    progress, 
    cancel, 
    pause, 
    resume,
    reset
  } = useExecution();
  
  // Dragging state
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  
  // Handle drag start
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Only drag from header area
    if ((e.target as HTMLElement).closest('.execution-controls')) return;
    
    isDragging.current = true;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isDragging.current) return;
      setPosition({
        x: moveEvent.clientX - dragOffset.current.x,
        y: moveEvent.clientY - dragOffset.current.y
      });
    };
    
    const handleMouseUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, []);
  
  // Don't render if never started
  if (!isExecuting && state.completedCount === 0 && state.totalCount === 0) {
    return null;
  }
  
  // Fix: Check for actual error string, not just !== null (undefined !== null is true!)
  const hasError = Boolean(state.error);
  const isComplete = !isExecuting && state.completedCount > 0 && !hasError;
  
  // Determine title status class
  const titleClass = hasError ? 'error' : isComplete ? 'complete' : isPaused ? 'paused' : 'running';
  
  // Build popup classes
  const popupClasses = ['execution-progress-popup'];
  if (isExecuting && !isPaused) popupClasses.push('running');
  if (position) popupClasses.push('dragged');
  
  // Custom position style if dragged
  const positionStyle = position ? {
    top: position.y,
    right: 'auto',
    left: position.x
  } : undefined;
  
  return createPortal(
    <div 
      className={popupClasses.join(' ')}
      style={positionStyle}
      onMouseDown={handleMouseDown}
    >
      <div className="execution-progress-card">
        {/* Header */}
        <div className="execution-progress-header">
          <h3 className={`execution-progress-title ${titleClass}`}>
            {isComplete ? '✓ Done' : 
             hasError ? '⚠ Error' : 
             isPaused ? '⏸ Paused' : 
             '⟳ Running'}
          </h3>
          <span className="execution-step-counter">
            {state.completedCount}/{state.totalCount}
          </span>
        </div>
        
        {/* Progress bar */}
        <div className="execution-progress-bar-container">
          <div 
            className={`execution-progress-bar ${hasError ? 'error' : ''} ${isComplete ? 'complete' : ''}`}
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Error message (truncated) */}
        {state.error && (
          <div className="execution-error-message" title={state.error}>
            {state.error.length > 40 ? state.error.substring(0, 40) + '...' : state.error}
          </div>
        )}
        
        {/* Controls */}
        <div className="execution-controls">
          {isExecuting && !isPaused && (
            <button className="execution-btn pause" onClick={pause}>
              ⏸
            </button>
          )}
          {isExecuting && isPaused && (
            <button className="execution-btn resume" onClick={resume}>
              ▶
            </button>
          )}
          {isExecuting && (
            <button className="execution-btn cancel" onClick={cancel}>
              ✕
            </button>
          )}
          {!isExecuting && (
            <button className="execution-btn close" onClick={reset}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ExecutionProgress;
